import { CaptionText, MaskType, Link } from '@paypalcorp/pp-react'
import CheckboxAdapter from 'ppbl/components/FinalFormComponents/CheckboxAdapter'
import DropdownFieldAdapter from 'ppbl/components/FinalFormComponents/DropdownAdapter'
import MaskedTextInputAdapter from 'ppbl/components/FinalFormComponents/MaskTextInputAdapter'
import PhoneInputAdapter from 'ppbl/components/FinalFormComponents/PhoneInputAdapter'
import RadioButtonAdapter from 'ppbl/components/FinalFormComponents/RadioButtonAdapter'
import TextInputAdapter from 'ppbl/components/FinalFormComponents/TextInputAdapter'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import { interpolate } from 'ppbl/utils/string-helper'
import {
  composeValidators,
  maxLength,
  maxCurrencyVal,
  valRange,
  valRangeWithPercentage,
  minCurrencyVal,
  required,
  isDate,
  isDOB,
  validFedTaxIDFormat,
  validSSNFormat,
  validEmail,
  validPhone,
  validZipCode,
  businessWorldreadyValidation,
  getFirstNameWorldreadyValidation,
  getLastNameWorldreadyValidation,
  worldreadyPhoneValidation,
  worldreadyAddressValidator,
  validationAddressPOBox,
  validFundingCode,
  validateWebAddress,
} from 'ppbl/utils/validation-helper'
import React from 'react'
import './styles.css'
import {
  applicationStateList,
  businessEntityTypeOptions,
  howDidYouHearAboutThisProductOptions,
  primaryLoanPurposeOptions,
} from './constants'

export const firstName = {
  name: getLocalizedText('application.applicationFields.firstName.name'),
  component: TextInputAdapter,
  label: getLocalizedText('application.applicationFields.firstName.label'),
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText('application.applicationFields.firstName.label'),
    validators: [getFirstNameWorldreadyValidation, maxLength(40)],
  }),
  fptiPage: 'applicationPage1',
}

export const lastName = {
  name: getLocalizedText('application.applicationFields.lastName.name'),
  component: TextInputAdapter,
  label: getLocalizedText('application.applicationFields.lastName.label'),
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText('application.applicationFields.lastName.label'),
    validators: [getLastNameWorldreadyValidation, maxLength(80)],
  }),
  fptiPage: 'applicationPage1',
}

export const phoneNumber = {
  name: getLocalizedText('application.applicationFields.phoneNumber.name'),
  component: PhoneInputAdapter,
  label: getLocalizedText('application.applicationFields.phoneNumber.label'),
  countryOptions: {
    phoneFormat: '(000) 000-0000',
    phoneRegion: 'US',
    countryPhoneCode: 1,
    countryName: 'United States',
  },
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText('application.applicationFields.phoneNumber.label'),
    validators: [required, validPhone],
  }),
}

export const phoneNumberType = {
  name: getLocalizedText('application.applicationFields.phoneNumberType.name'),
  component: RadioButtonAdapter,
  options: [
    {
      label: getLocalizedText(
        'application.applicationFields.phoneNumberType.options.label1',
      ),
      value: getLocalizedText(
        'application.applicationFields.phoneNumberType.options.value1',
      ),
    },
    {
      label: getLocalizedText(
        'application.applicationFields.phoneNumberType.options.label2',
      ),
      value: getLocalizedText(
        'application.applicationFields.phoneNumberType.options.value2',
      ),
    },
    {
      label: getLocalizedText(
        'application.applicationFields.phoneNumberType.options.label3',
      ),
      value: getLocalizedText(
        'application.applicationFields.phoneNumberType.options.value3',
      ),
    },
  ],
  style: { flexDirection: 'row', gap: '1rem' },
  className: 'radioButtonField',
  radioLabelClassName: 'hide',
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.phoneNumberType.label',
    ),
    validators: [required],
  }),
}

export const emailAddress = {
  name: getLocalizedText('application.applicationFields.emailAddress.name'),
  component: TextInputAdapter,
  label: getLocalizedText('application.applicationFields.emailAddress.label'),
  type: 'email',
  validate: composeValidators({
    label: getLocalizedText('application.applicationFields.emailAddress.label'),
    validators: [required, maxLength(80), validEmail],
  }),
  fptiPage: 'applicationPage1',
}

export const primaryLoanPurpose = {
  name: getLocalizedText(
    'application.applicationFields.primaryLoanPurpose.name',
  ),
  component: DropdownFieldAdapter,
  label: getLocalizedText(
    'application.applicationFields.primaryLoanPurpose.label',
  ),
  options: primaryLoanPurposeOptions,
  validate: (value) =>
    required(
      value,
      getLocalizedText(
        'application.applicationFields.primaryLoanPurpose.label',
      ),
    ),
  fptiPage: 'applicationPage1',
}

export const howDidYouHearAboutThisProduct = {
  name: getLocalizedText(
    'application.applicationFields.howDidYouHearAboutThisProduct.name',
  ),
  component: DropdownFieldAdapter,
  label: getLocalizedText(
    'application.applicationFields.howDidYouHearAboutThisProduct.label',
  ),
  options: howDidYouHearAboutThisProductOptions,
  validate: (value) =>
    required(
      value,
      getLocalizedText(
        'application.applicationFields.howDidYouHearAboutThisProduct.validationLabel',
      ),
    ),
  fptiPage: 'applicationPage1',
}

export const doYouHaveAFundingCode = {
  name: getLocalizedText(
    'application.applicationFields.doYouHaveAFundingCode.name',
  ),
  component: MaskedTextInputAdapter,
  label: getLocalizedText(
    'application.applicationFields.doYouHaveAFundingCode.label',
  ),
  toolTip: getLocalizedText(
    'application.applicationFields.doYouHaveAFundingCode.toolTip',
  ),
  maskOptions: {
    mask: '#####-a00000000',
    definitions: {
      '#': /[0-9a-zA-Z]/,
    },
    lazy: true,
    overwrite: 'shift',
  },
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.doYouHaveAFundingCode.validationLabel',
    ),
    validators: [validFundingCode],
  }),
  type: 'text',
  fptiPage: 'applicationPage1',
}

export const agreedToTermsAndConditions = {
  name: getLocalizedText(
    'application.applicationFields.agreedToTermsAndConditions.name',
  ),
  type: 'checkbox',
  'aria-label': interpolate(
    getLocalizedText(
      'application.applicationPages.applicationPage1.checkBoxLabel',
    ),
    {
      termsAndConditions: (
        <Link href="/" size="sm" secondary>
          {getLocalizedText(
            'application.applicationPages.applicationPage1.termsAndConditionsLabel',
          )}
        </Link>
      ),
      privacyPolicy: (
        <Link href="/" size="sm" secondary>
          {getLocalizedText(
            'application.applicationPages.applicationPage1.privacyPolicyLabel',
          )}
        </Link>
      ),
    },
  ),
  component: CheckboxAdapter,
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.agreedToTermsAndConditions.validationLabel',
    ),
    validators: [required],
  }),
  fptiPage: 'applicationPage1',
}

export const homeAddress1 = {
  name: getLocalizedText('application.applicationFields.homeAddress1.name'),
  component: TextInputAdapter,
  label: getLocalizedText('application.applicationFields.homeAddress1.label'),
  type: 'text',
  helperText: getLocalizedText(
    'application.applicationFields.homeAddress1.helperText',
  ),
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.homeAddress1.validationLabel',
    ),
    validators: [
      worldreadyAddressValidator,
      validationAddressPOBox,
      maxLength(255),
    ],
  }),
  fptiPage: 'applicationPage2',
}

export const homeAddress2 = {
  name: getLocalizedText('application.applicationFields.homeAddress2.name'),
  component: TextInputAdapter,
  label: getLocalizedText('application.applicationFields.homeAddress2.label'),
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.homeAddress2.validationLabel',
    ),
    validators: [worldreadyAddressValidator, validationAddressPOBox],
  }),
  fptiPage: 'applicationPage2',
}

export const city = {
  name: getLocalizedText('application.applicationFields.city.name'),
  component: TextInputAdapter,
  label: getLocalizedText('application.applicationFields.city.label'),
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText('application.applicationFields.city.label'),
    validators: [worldreadyAddressValidator, maxLength(80)],
  }),
  fptiPage: 'applicationPage2',
}

export const state = {
  name: getLocalizedText('application.applicationFields.state.name'),
  component: DropdownFieldAdapter,
  label: getLocalizedText('application.applicationFields.state.label'),
  options: applicationStateList,
  validate: composeValidators({
    label: getLocalizedText('application.applicationFields.state.label'),
    validators: [worldreadyAddressValidator],
  }),
  fptiPage: 'applicationPage2',
}

export const zip = {
  name: getLocalizedText('application.applicationFields.zip.name'),
  component: MaskedTextInputAdapter,
  label: getLocalizedText('application.applicationFields.zip.label'),
  maskOptions: {
    mask: '00000',
    lazy: true,
    overwrite: 'shift',
  },
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText('application.applicationFields.zip.label'),
    validators: [worldreadyAddressValidator, validZipCode],
  }),
  fptiPage: 'applicationPage2',
}

export const personalPhoneNumber = {
  name: getLocalizedText(
    'application.applicationFields.personalPhoneNumber.name',
  ),
  component: PhoneInputAdapter,
  label: getLocalizedText(
    'application.applicationFields.personalPhoneNumber.label',
  ),
  countryOptions: {
    phoneFormat: '(000) 000-0000',
    phoneRegion: 'US',
    countryPhoneCode: 1,
    countryName: 'United States',
  },
  type: 'text',
  helperText: getLocalizedText(
    'application.applicationFields.personalPhoneNumber.helperText',
  ),
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.personalPhoneNumber.label',
    ),
    validators: [worldreadyPhoneValidation, validPhone],
  }),
  fptiPage: 'applicationPage2',
}

export const personalPhoneNumberType = {
  name: getLocalizedText(
    'application.applicationFields.personalPhoneNumberType.name',
  ),
  component: RadioButtonAdapter,
  options: [
    {
      label: getLocalizedText(
        'application.applicationFields.personalPhoneNumberType.options.label1',
      ),
      value: getLocalizedText(
        'application.applicationFields.personalPhoneNumberType.options.value1',
      ),
    },
    {
      label: getLocalizedText(
        'application.applicationFields.personalPhoneNumberType.options.label2',
      ),
      value: getLocalizedText(
        'application.applicationFields.personalPhoneNumberType.options.value2',
      ),
    },
  ],
  style: { flexDirection: 'row', gap: '1rem' },
  className: 'radioButtonField',
  radioLabelClassName: 'hide',
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.personalPhoneNumberType.label',
    ),
    validators: [required],
  }),
  fptiPage: 'applicationPage2',
}

export const homeBasedBusiness = {
  name: getLocalizedText(
    'application.applicationFields.homeBasedBusiness.name',
  ),
  component: RadioButtonAdapter,
  label: getLocalizedText(
    'application.applicationFields.homeBasedBusiness.label',
  ),
  options: [
    {
      label: getLocalizedText(
        'application.applicationFields.homeBasedBusiness.options.label1',
      ),
      value: getLocalizedText(
        'application.applicationFields.homeBasedBusiness.options.value1',
      ),
    },
    {
      label: getLocalizedText(
        'application.applicationFields.homeBasedBusiness.options.label2',
      ),
      value: getLocalizedText(
        'application.applicationFields.homeBasedBusiness.options.value2',
      ),
    },
  ],
  style: { flexDirection: 'row', gap: '1rem' },
  className: 'radioButtonField',
  radioLabelClassName: 'hide',
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.homeBasedBusiness.validatorLabel',
    ),
    validators: [required],
  }),
  fptiPage: 'applicationPage3',
}

export const businessAddress1 = {
  name: getLocalizedText('application.applicationFields.businessAddress1.name'),
  component: TextInputAdapter,
  label: getLocalizedText(
    'application.applicationFields.businessAddress1.label',
  ),
  type: 'text',
  helperText: getLocalizedText(
    'application.applicationFields.businessAddress1.helperText',
  ),
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.businessAddress1.validationLabel',
    ),
    validators: [
      worldreadyAddressValidator,
      validationAddressPOBox,
      maxLength(255),
    ],
  }),
  fptiPage: 'applicationPage3',
}

export const businessAddress2 = {
  name: getLocalizedText('application.applicationFields.businessAddress2.name'),
  component: TextInputAdapter,
  label: getLocalizedText(
    'application.applicationFields.businessAddress2.label',
  ),
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.businessAddress2.validationLabel',
    ),
    validators: [worldreadyAddressValidator, validationAddressPOBox],
  }),
  fptiPage: 'applicationPage3',
}

export const businessCity = {
  name: getLocalizedText('application.applicationFields.businessCity.name'),
  component: TextInputAdapter,
  label: getLocalizedText('application.applicationFields.businessCity.label'),
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText('application.applicationFields.businessCity.label'),
    validators: [worldreadyAddressValidator, maxLength(80)],
  }),
  fptiPage: 'applicationPage3',
}

export const businessState = {
  name: getLocalizedText('application.applicationFields.businessState.name'),
  component: DropdownFieldAdapter,
  label: getLocalizedText('application.applicationFields.businessState.label'),
  options: applicationStateList,
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.businessState.label',
    ),
    validators: [worldreadyAddressValidator],
  }),
  fptiPage: 'applicationPage3',
}

export const businessZip = {
  name: getLocalizedText('application.applicationFields.businessZip.name'),
  component: MaskedTextInputAdapter,
  label: getLocalizedText('application.applicationFields.businessZip.label'),
  maskOptions: {
    mask: '00000',
    lazy: true,
    overwrite: 'shift',
  },
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText('application.applicationFields.zip.label'),
    validators: [worldreadyAddressValidator, validZipCode],
  }),
  fptiPage: 'applicationPage3',
}

export const businessPhoneNumber = {
  name: getLocalizedText(
    'application.applicationFields.businessPhoneNumber.name',
  ),
  component: PhoneInputAdapter,
  label: getLocalizedText(
    'application.applicationFields.businessPhoneNumber.label',
  ),
  countryOptions: {
    phoneFormat: '(000) 000-0000',
    phoneRegion: 'US',
    countryPhoneCode: 1,
    countryName: 'United States',
  },
  type: 'text',
  helperText: getLocalizedText(
    'application.applicationFields.businessPhoneNumber.helperText',
  ),
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.businessPhoneNumber.label',
    ),
    validators: [worldreadyPhoneValidation, validPhone],
  }),
  fptiPage: 'applicationPage3',
}

export const businessWebAddress = {
  name: getLocalizedText(
    'application.applicationFields.businessWebAddress.name',
  ),
  component: TextInputAdapter,
  type: 'text',
  label: getLocalizedText(
    'application.applicationFields.businessWebAddress.label',
  ),
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.businessWebAddress.errorLabel',
    ),
    validators: [validateWebAddress],
  }),
  fptiPage: 'applicationPage3',
}

export const businessEntityType = {
  name: getLocalizedText(
    'application.applicationFields.businessEntityType.name',
  ),
  component: RadioButtonAdapter,
  label: getLocalizedText(
    'application.applicationFields.businessEntityType.label',
  ),
  options: businessEntityTypeOptions,
  className: 'radioButtonField',
  radioLabelClassName: 'hide',
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.businessEntityType.validationLabel',
    ),
    validators: [required],
  }),
  fptiPage: 'applicationPage4',
}

export const legalBusinessName = {
  name: getLocalizedText(
    'application.applicationFields.legalBusinessName.name',
  ),
  component: TextInputAdapter,
  label: getLocalizedText(
    'application.applicationFields.legalBusinessName.label',
  ),
  type: 'text',
  helperText: getLocalizedText(
    'application.applicationFields.legalBusinessName.helperText',
  ),
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.legalBusinessName.label',
    ),
    validators: [businessWorldreadyValidation, maxLength(255)],
  }),
  fptiPage: 'applicationPage1',
}

export const doingBusinessAsRadio = {
  name: getLocalizedText(
    'application.applicationFields.doingBusinessAsRadio.name',
  ),
  component: RadioButtonAdapter,
  label: getLocalizedText(
    'application.applicationFields.doingBusinessAsRadio.label',
  ),
  options: [
    {
      label: getLocalizedText(
        'application.applicationFields.doingBusinessAsRadio.options.label1',
      ),
      value: getLocalizedText(
        'application.applicationFields.doingBusinessAsRadio.options.value1',
      ),
    },
    {
      label: getLocalizedText(
        'application.applicationFields.doingBusinessAsRadio.options.label2',
      ),
      value: getLocalizedText(
        'application.applicationFields.doingBusinessAsRadio.options.value2',
      ),
    },
  ],
  style: { flexDirection: 'row', gap: '1rem' },
  className: 'radioButtonField',
  radioLabelClassName: 'hide',
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.doingBusinessAsRadio.validationLabel',
    ),
    validators: [required],
  }),
  fptiPage: 'applicationPage4',
}

export const doingBusinessAs = {
  name: getLocalizedText('application.applicationFields.doingBusinessAs.name'),
  component: TextInputAdapter,
  label: getLocalizedText(
    'application.applicationFields.doingBusinessAs.label',
  ),
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.doingBusinessAs.validationLabel',
    ),
    validators: [required, maxLength(255)],
  }),
  fptiPage: 'applicationPage4',
}

export const stateOfIncorporation = {
  name: getLocalizedText(
    'application.applicationFields.stateOfIncorporation.name',
  ),
  component: DropdownFieldAdapter,
  label: getLocalizedText(
    'application.applicationFields.stateOfIncorporation.label',
  ),
  options: applicationStateList,
  validate: (value) =>
    required(
      value,
      getLocalizedText(
        'application.applicationFields.stateOfIncorporation.label',
      ),
    ),
  fptiPage: 'applicationPage4',
}

export const totalAnnualBusinessRevenue = {
  name: getLocalizedText(
    'application.applicationFields.totalAnnualBusinessRevenue.name',
  ),
  component: MaskedTextInputAdapter,
  label: getLocalizedText(
    'application.applicationFields.totalAnnualBusinessRevenue.label',
  ),
  prefix: '$',
  maskOptions: {
    mask: Number,
    thousandsSeparator: ',',
    min: 0,
    max: 99999999999,
    lazy: true,
  },
  type: 'text',
  helperText: getLocalizedText(
    'application.applicationFields.totalAnnualBusinessRevenue.helperText',
  ),
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.totalAnnualBusinessRevenue.label',
    ),
    validators: [required, maxCurrencyVal(999999999), minCurrencyVal(10000)],
  }),
  fptiPage: 'applicationPage4',
}

export const businessStartDate = {
  name: getLocalizedText(
    'application.applicationFields.businessStartDate.name',
  ),
  component: MaskedTextInputAdapter,
  label: getLocalizedText(
    'application.applicationFields.businessStartDate.label',
  ),
  maskOptions: {
    mask: 'm/y',
    blocks: {
      m: {
        mask: MaskType.Range,
        from: 1,
        to: 12,
        maxLength: 2,
      },
      y: {
        mask: MaskType.Range,
        from: 1900,
        to: 9999,
        maxLength: 4,
      },
    },
    lazy: true,
  },
  inputValueType: 'value',
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.businessStartDate.validationLabel',
    ),
    validators: [required, isDate],
  }),
  fptiPage: 'applicationPage4',
}

export const numberOfFullTimeEmployees = {
  name: getLocalizedText(
    'application.applicationFields.numberOfFullTimeEmployees.name',
  ),
  component: MaskedTextInputAdapter,
  label: getLocalizedText(
    'application.applicationFields.numberOfFullTimeEmployees.label',
  ),
  maskOptions: {
    mask: 'num',
    blocks: {
      num: {
        mask: Number,
        min: 0,
        max: 9999,
      },
    },
    overwrite: 'shift',
    lazy: true,
  },
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.numberOfFullTimeEmployees.label',
    ),
    validators: [required, valRange(1, 999)],
  }),
  fptiPage: 'applicationPage4',
}

export const businessIndustry = {
  name: getLocalizedText('application.applicationFields.businessIndustry.name'),
  component: DropdownFieldAdapter,
  label: getLocalizedText(
    'application.applicationFields.businessIndustry.label',
  ),
  validate: (value) =>
    required(
      value,
      getLocalizedText('application.applicationFields.businessIndustry.label'),
    ),
  fptiPage: 'applicationPage4',
}

export const businessSubIndustry = {
  name: getLocalizedText(
    'application.applicationFields.businessSubIndustry.name',
  ),
  component: DropdownFieldAdapter,
  label: getLocalizedText(
    'application.applicationFields.businessSubIndustry.label',
  ),
  validate: (value) =>
    required(
      value,
      getLocalizedText(
        'application.applicationFields.businessSubIndustry.label',
      ),
    ),
  fptiPage: 'applicationPage4',
}

export const dateOfBirth = {
  name: getLocalizedText('application.applicationFields.dateOfBirth.name'),
  component: MaskedTextInputAdapter,
  label: getLocalizedText('application.applicationFields.dateOfBirth.label'),
  maskOptions: {
    mask: 'm/d/y',
    blocks: {
      m: {
        mask: MaskType.Range,
        from: 1,
        to: 12,
        maxLength: 2,
      },
      d: {
        mask: MaskType.Range,
        from: 1,
        to: 31,
        maxLength: 2,
      },
      y: {
        mask: MaskType.Range,
        from: 1900,
        to: 9999,
        maxLength: 4,
      },
    },
    lazy: true,
  },
  inputValueType: 'value',
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText('application.applicationFields.dateOfBirth.label'),
    validators: [required, isDOB],
  }),
  fptiPage: 'applicationPage5',
}

export const socialSecurityNumber = {
  name: getLocalizedText(
    'application.applicationFields.socialSecurityNumber.name',
  ),
  component: MaskedTextInputAdapter,
  label: getLocalizedText(
    'application.applicationFields.socialSecurityNumber.label',
  ),
  maskOptions: {
    mask: '000-00-0000',
    lazy: true,
    overwrite: 'shift',
  },
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.socialSecurityNumber.label',
    ),
    validators: [required, validSSNFormat],
  }),
  fptiPage: 'applicationPage5',
}

export const federalTaxId = {
  name: getLocalizedText('application.applicationFields.federalTaxId.name'),
  component: MaskedTextInputAdapter,
  label: getLocalizedText('application.applicationFields.federalTaxId.label'),
  maskOptions: {
    mask: '00-0000000',
    lazy: true,
    overwrite: 'shift',
  },
  type: 'text',
  helperText: getLocalizedText(
    'application.applicationFields.federalTaxId.helperText',
  ),
  validate: composeValidators({
    label: getLocalizedText('application.applicationFields.federalTaxId.label'),
    validators: [required, validFedTaxIDFormat],
  }),
  fptiPage: 'applicationPage5',
}

export const percentageOfOwnership = {
  name: getLocalizedText(
    'application.applicationFields.percentageOfOwnership.name',
  ),
  component: MaskedTextInputAdapter,
  label: getLocalizedText(
    'application.applicationFields.percentageOfOwnership.label',
  ),
  maskOptions: {
    mask: 'num%',
    blocks: {
      num: {
        mask: Number,
        min: 0,
        max: 100,
      },
    },
    lazy: false,
    overwrite: 'shift',
  },
  type: 'text',
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.percentageOfOwnership.validationLabel',
    ),
    validators: [required, valRangeWithPercentage(0, 100)],
  }),
  fptiPage: 'applicationPage5',
}

export const isControllingManager = {
  name: getLocalizedText(
    'application.applicationFields.isControllingManager.name',
  ),
  component: RadioButtonAdapter,
  label: getLocalizedText(
    'application.applicationFields.isControllingManager.label',
  ),
  options: [
    {
      label: getLocalizedText(
        'application.applicationFields.isControllingManager.options.label1',
      ),
      value: getLocalizedText(
        'application.applicationFields.isControllingManager.options.value1',
      ),
    },
    {
      label: getLocalizedText(
        'application.applicationFields.isControllingManager.options.label2',
      ),
      value: getLocalizedText(
        'application.applicationFields.isControllingManager.options.value2',
      ),
    },
  ],
  style: { flexDirection: 'row', gap: '1rem' },
  className: 'radioButtonField',
  radioLabelClassName: 'hide',
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.isControllingManager.validationLabel',
    ),
    validators: [required],
  }),
  fptiPage: 'applicationPage5',
}

export const electronicCommunications = {
  name: getLocalizedText(
    'application.applicationFields.electronicCommunications.name',
  ),
  type: 'checkbox',
  label: (
    <CaptionText>
      {getLocalizedText(
        'application.applicationFields.electronicCommunications.label',
      )}
    </CaptionText>
  ),
  component: CheckboxAdapter,
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.electronicCommunications.validationLabel',
    ),
    validators: [required],
  }),
  fptiPage: 'applicationPage5',
}

export const writtenInstructions = {
  name: getLocalizedText(
    'application.applicationFields.writtenInstructions.name',
  ),
  type: 'checkbox',
  label: (
    <CaptionText>
      {getLocalizedText(
        'application.applicationFields.writtenInstructions.label',
      )}
    </CaptionText>
  ),
  component: CheckboxAdapter,
  validate: composeValidators({
    label: getLocalizedText(
      'application.applicationFields.writtenInstructions.validationLabel',
    ),
    validators: [required],
  }),
  fptiPage: 'applicationPage5',
}
